import { DateRangeOutlined, KeyboardArrowDownRounded } from "@mui/icons-material";
import { Button, MenuItem, MenuList, Popover } from "@mui/material";
import { DateRange, DateRangeCalendar, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { useState } from "react";

type DateRangeOption = "yesterday" | "7days" | "30days" | "90days" | "custom";

interface IDateParams {
  startDateUtc: string;
  endDateUtc: string;
}

interface IProps {
  onChange: (params: IDateParams) => void;
}

const presetOptions: { value: Exclude<DateRangeOption, "custom">; label: string }[] = [
  { value: "yesterday", label: "Yesterday" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "90days", label: "Last 90 Days" },
];

const toPresetParams = (option: Exclude<DateRangeOption, "custom">): IDateParams => {
  const now = DateTime.now();
  switch (option) {
    case "yesterday": {
      const yesterday = now.minus({ days: 1 });
      return { startDateUtc: yesterday.startOf("day").toISO(), endDateUtc: yesterday.endOf("day").toISO() };
    }
    case "7days":
      return { startDateUtc: now.minus({ days: 7 }).startOf("day").toISO(), endDateUtc: now.endOf("day").toISO() };
    case "30days":
      return { startDateUtc: now.minus({ days: 30 }).startOf("day").toISO(), endDateUtc: now.endOf("day").toISO() };
    case "90days":
      return { startDateUtc: now.minus({ days: 90 }).startOf("day").toISO(), endDateUtc: now.endOf("day").toISO() };
  }
};

const AppUsageDateRangePicker = (props: IProps) => {
  const [selectedOption, setSelectedOption] = useState<DateRangeOption>("30days");
  const [customDateRange, setCustomDateRange] = useState<DateRange<DateTime>>([null, null]);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const handleClose = () => {
    setMenuAnchor(null);
    setShowCustomPicker(false);
  };

  const selectedLabel = (() => {
    if (selectedOption === "custom") {
      const [start, end] = customDateRange;
      if (start && end) return `${start.toFormat("MMM d")} - ${end.toFormat("MMM d")}`;
      return "Custom Range";
    }
    return presetOptions.find((o) => o.value === selectedOption)?.label;
  })();

  return (
    <>
      <Button
        variant="tertiary"
        startIcon={<DateRangeOutlined />}
        endIcon={<KeyboardArrowDownRounded />}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
      >
        {selectedLabel}
      </Button>

      <Popover
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { borderRadius: "10px" } } }}
      >
        {showCustomPicker ? (
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateRangeCalendar
              calendars={2}
              value={customDateRange}
              onChange={(range) => {
                setCustomDateRange(range);
                if (range[0] && range[1]) {
                  setSelectedOption("custom");
                  props.onChange({
                    startDateUtc: range[0].startOf("day").toISO()!,
                    endDateUtc: range[1].endOf("day").toISO()!,
                  });
                  handleClose();
                }
              }}
            />
          </LocalizationProvider>
        ) : (
          <MenuList>
            {presetOptions.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === selectedOption}
                onClick={() => {
                  setSelectedOption(option.value);
                  props.onChange(toPresetParams(option.value));
                  handleClose();
                }}
              >
                {option.label}
              </MenuItem>
            ))}
            <MenuItem
              selected={selectedOption === "custom"}
              onClick={() => {
                setCustomDateRange([null, null]);
                setShowCustomPicker(true);
              }}
            >
              Custom Range...
            </MenuItem>
          </MenuList>
        )}
      </Popover>
    </>
  );
};

export { AppUsageDateRangePicker };
export type { DateRangeOption };
